import { reportIssue } from './Monitoring';
import { showDialog } from 'Subsystems/dialog/actions.ts';
import GenericErrorDialog from 'Components/dialogs/GenericErrorDialog';
import { Store } from 'redux';

let errorInterceptor: ((status: number, errorData?: any) => void) | null = null;
export const setupErrorHandling = (store: Store) => {
  errorInterceptor = (status: number, errorData?: any) => {
    if (status === 0 || status >= 500) {
      store.dispatch(
        showDialog(GenericErrorDialog, {
          title: 'Something went wrong',
          description:
            'There was an error communicating with the server. Try refreshing your browser.',
        }),
      );
      return;
    }
    switch (status) {
      case 401: {
        store.dispatch(
          showDialog(GenericErrorDialog, {
            title: 'Your session expired',
            description:
              'It seems your session has expired. Please log in again.',
            action: () => {
              window.location.reload();
            },
          }),
        );
        return;
      }
      case 403: {
        store.dispatch(
          showDialog(GenericErrorDialog, {
            title: 'Insufficient permissions',
            description:
              'It seems your account does not have permission to do that. Please contact an admin to get access.',
          }),
        );
        return;
      }
      case 400: {
        if (!errorData?.message) return;
        store.dispatch(
          showDialog(GenericErrorDialog, {
            title: 'Request error',
            description: errorData?.message,
          }),
        );
        return;
      }
    }
  };
};

export const post = async <B, R>(url: string, data: B): Promise<R> => {
  const response = await fetchWithPayload('POST', url, data);

  await handleError(response, 'POST', data);
  return parseBody<R>(response);
};

interface UploadWithProgressConfig {
  onUploadProgress: (progressEvent: ProgressEvent) => void;
  signal: AbortSignal;
}
export const uploadWithProgress = async (
  url: string,
  formData: FormData,
  config: UploadWithProgressConfig,
) => {
  const { onUploadProgress, signal } = config;
  const xhr = new XMLHttpRequest();

  // Listen for progress events
  xhr.upload.addEventListener('progress', onUploadProgress);

  const xhrPromise = new Promise<void>((resolve, reject) => {
    xhr.addEventListener('load', () => {
      const { status, responseText } = xhr;
      if (status >= 200 && status < 300) {
        resolve();
        return;
      }
      errorInterceptor?.(status);
      reject({
        status,
        message: 'An error occurred during the upload ' + responseText,
      });
    });

    xhr.addEventListener('error', () => {
      errorInterceptor?.(0);
      reject({
        message: 'An error occurred during the upload',
      });
    });
    xhr.addEventListener('abort', () => {
      errorInterceptor?.(0);
      reject({
        message: 'An error occurred during the upload',
      });
    });

    signal?.addEventListener('abort', () => {
      reject('Aborted');
      xhr.abort();
    });
  });

  xhr.open('POST', url, true);
  xhr.send(formData);

  return xhrPromise;
};

export const put = async <B, R>(url: string, data: B): Promise<R> => {
  const response = await fetchWithPayload('PUT', url, data);

  await handleError(response, 'PUT', data);
  return parseBody<R>(response);
};

export const del = async <R>(url: string): Promise<R> => {
  let response: Response | null = null;
  try {
    response = await fetch(url, {
      method: 'DELETE',
    });
  } catch (e) {
    console.error('Unable to call API' + url, e);
  }

  await handleError(response, 'DELETE');
  return parseBody<R>(response);
};

export const get = async <R>(
  url: string,
  params?: Record<string, string>,
): Promise<R> => {
  const requestUrl = params
    ? `${url}?${new URLSearchParams(sanitizeParams(params))}`
    : url;
  let response: Response | null = null;
  try {
    response = await fetch(requestUrl);
  } catch (e) {
    console.error('Unable to call API' + requestUrl, e);
  }

  await handleError(response, 'GET');
  return parseBody<R>(response);
};

const fetchWithPayload = async (
  method: 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: unknown,
): Promise<Response | null> => {
  const isForm = data instanceof FormData;
  const isString = typeof data === 'string';

  const headers = isForm
    ? undefined
    : {
        'Content-Type': isString ? 'text/plain' : 'application/json',
      };
  try {
    return await fetch(url, {
      method,
      headers,
      body: isForm
        ? (data as FormData)
        : isString
          ? (data as string)
          : JSON.stringify(data ?? undefined),
    });
  } catch (e) {
    console.error('Unable to call API' + url, e);
    return null;
  }
};

const parseBody = async <R>(response: Response | null): Promise<R | null> => {
  if (!response) return null;

  const contentType = response.headers.get('Content-Type');
  if (contentType?.startsWith('text')) {
    return (await response.text()) as R;
  }
  const responseClone = response.clone();
  try {
    return (await response.json()) as R;
  } catch (_error) {
    return (await responseClone.text()) as R;
  }
};

const handleError = async (
  response: Response | null,
  method: HTTPMethod,
  data?: any,
) => {
  if (response?.ok) return;

  const errorData = await parseBody(response);

  errorInterceptor?.(response?.status ?? 0, errorData);

  if (errorData) {
    throw {
      status: response?.status ?? 0,
      data: errorData,
    };
  }

  if (response) {
    // we have a response but no error data
    reportIssue(`No API response`, {
      tags: { eng: 'teddy', issue: 'no-api-response' },
      data: {
        requestMethod: method,
        requestUrl: response?.url,
        errorCode: response?.status,
      },
    });

    try {
      console.log('Pre-serialization request debug', data);
    } catch (err) {
      console.log(err);
    }
  }
  throw undefined;
};

const sanitizeParams = (obj: Record<string, string>) => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => [key, value]),
  ) as Record<string, string>;
};

type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** see FrontendRedirectCommand */
export interface FrontendRedirectCommand {
  redirectUrl: string;
}

export function handleRedirect(command: FrontendRedirectCommand) {
  // reload if the same
  if (window.location.href == command.redirectUrl) {
    window.location.reload();
  } else {
    // send them off to a new destination
    window.location.href = command.redirectUrl;
  }
}

/**
 * Below is a legacy compatibility layer for code that used to call axios directly.
 * It'll be replaced over time and then removed
 */
interface AxiosResponse {
  status: number;
  data: any;
}

interface AxiosConfig {
  data?: unknown;
  params?: any;
}

const handleLegacyError = async (response: Response | null): Promise<void> => {
  if (response?.ok) return;

  const errorData = await parseBody(response);

  errorInterceptor?.(response?.status ?? 0, errorData);

  throw {
    response: {
      status: response?.status ?? 0,
      data: errorData ?? { message: 'Something went wrong' },
    },
  };
};

/**
 * This is a legacy function that mimics axios API. It uses fetch under the hood
 * This should be replaced by calls to rest.ts or fetch directly
 * @deprecated
 */
export const legacyGet = async (
  url: string,
  config?: AxiosConfig,
): Promise<AxiosResponse> => {
  const requestUrl =
    url +
    (config?.params
      ? '?' + new URLSearchParams(sanitizeParams(config?.params)).toString()
      : '');

  let response: Response | null = null;
  try {
    response = await fetch(requestUrl);
  } catch (e) {
    console.error('Unable to call API' + requestUrl, e);
  }
  await handleLegacyError(response);
  return {
    status: response?.status,
    data: await parseBody(response),
  };
};

/**
 * This is a legacy function that mimics axios API. It uses fetch under the hood
 * This should be replaced by calls to rest.ts or fetch directly
 * @deprecated
 */
export const legacyPost = async (
  url: string,
  data?: unknown,
): Promise<AxiosResponse> => {
  const response = await fetchWithPayload('POST', url, data);

  await handleLegacyError(response);
  return {
    status: response.status,
    data: await parseBody(response),
  };
};

/**
 * This is a legacy function that mimics axios API. It uses fetch under the hood
 * This should be replaced by calls to rest.ts or fetch directly
 * @deprecated
 */
export const legacyPut = async (
  url: string,
  data: unknown,
): Promise<AxiosResponse> => {
  const response = await fetchWithPayload('PUT', url, data);

  await handleLegacyError(response);
  return {
    status: response.status,
    data: await parseBody(response),
  };
};

/**
 * This is a legacy function that mimics axios API. It uses fetch under the hood
 * This should be replaced by calls to rest.ts or fetch directly
 * @deprecated
 */
export const legacyDelete = async (
  url: string,
  config?: AxiosConfig,
): Promise<AxiosResponse> => {
  const response = await fetchWithPayload('DELETE', url, config?.data);

  await handleLegacyError(response);
  return {
    status: response.status,
    data: await parseBody(response),
  };
};
